import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { Box, Image } from '@chakra-ui/react'
import { EDU_LEVELS_SLUG, POST_SLUG, PAGE_SLUG, REGISTRATION_SLUG } from '../../lib/constants'
import PdfLink from './pdfLink'
import { localisedLink } from '../../utils/utils'
import TypeformEmbed from './typeformEmded'

const UnOrderedList = styled.ul`
    margin-top: 20px;
    display: block;
    margin-left: 0 !important;
    padding-left: 0 !important;

    li {
        list-style: circle;
        margin: 5px 0 0 30px;
    }

    li div.paragraph {
        margin: 0;
    }
`

const RichContent = ({ body, locale }) => {
    if (body === null || body === undefined) {
        return ''
    }

    const embeddedAssets = extractEmbeddedAssets(body)
    const typeformEmbeds = extractTypeformEmbedAssets(body)
    const linkToPosts = extractLinkToPosts(body)
    const linkToPage = extractLinkToPage(body)
    const linkToRegistration = extractLinkToRegistration(body)
    const linkToEducationalLevels = extractLinkToEducationalLevels(body)

    const allEntryTypes = { ...linkToPosts, ...linkToPage, ...linkToRegistration, ...linkToEducationalLevels, ...typeformEmbeds }

    const options = {
        renderMark: {
            [MARKS.BOLD]: (text) => <strong>{text}</strong>,
        },
        renderNode: {
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
                const entry = allEntryTypes[node.data.target.sys.id]

                switch (entry?.internal?.type) {
                    case 'ContentfulEducationalLevels':
                        return (
                            <Link to={localisedLink(locale, EDU_LEVELS_SLUG, entry.slug)}>
                                <span>{children}</span>
                            </Link>
                        )
                    case 'ContentfulPosts':
                        return (
                            <Link to={localisedLink(locale, POST_SLUG, entry.slug)}>
                                <span>{children}</span>
                            </Link>
                        )
                    case 'ContentfulPage':
                        return (
                            <Link to={localisedLink(locale, PAGE_SLUG, entry.slug)}>
                                <span>{children}</span>
                            </Link>
                        )
                    case 'ContentfulRegistrations':
                        return (
                            <Link to={localisedLink(locale, REGISTRATION_SLUG, entry.slug)}>
                                <span>{children}</span>
                            </Link>
                        )
                    default:
                        return <span>{children}</span>
                }
            },
            [INLINES.ASSET_HYPERLINK]: (node, children) => {
                // COMPLETE: link text to an image
                const asset = embeddedAssets[node.data.target.sys.id]
                return (
                    <a href={asset?.url} target="_blank" rel="noreferrer">
                        {children}
                    </a>
                )
                // switch (asset.mimeType) {
                //     case 'application/pdf': {
                //         return <PdfLink pdfUrl={asset?.url}>{children}</PdfLink>
                //     }
                //     default: {
                //         return (
                //             <a href={asset?.url} target="_blank" rel="noreferrer">
                //                 {children}
                //             </a>
                //         )
                //     }
                // }
            },
            [INLINES.HYPERLINK]: (node, children) => {
                if (node.data.uri.includes('youtube.com') || node.data.uri.includes('youtu.be')) {
                    // Extract videoId from the URL
                    const match = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/.exec(
                        node.data.uri
                    )
                    const videoId = match && match[7].length === 11 ? match[7] : null
                    return (
                        videoId && (
                            <section className="video-container">
                                <iframe
                                    className="video"
                                    title={`https://youtube.com/embed/${videoId}`}
                                    src={`https://youtube.com/embed/${videoId}?rel=0&autoplay=0&showinfo=0&modestbranding=true`}
                                    allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </section>
                        )
                    )
                }

                return (
                    <a target="_blank" rel="noreferrer" className="inlineLink" href={node.data.uri}>
                        {children}
                    </a>
                )
            },
            [BLOCKS.EMBEDDED_ASSET]: (node) => {
                const asset = embeddedAssets[node.data.target.sys.id]

                switch (asset?.mimeType) {
                    case 'video/mp4':
                        return (
                            <div>
                                <video width="1024" height="900" controls>
                                    <source src={asset.url} type="video/mp4" />
                                    <track />
                                </video>
                            </div>
                        )
                    case 'image/png':
                    case 'image/jpg':
                    case 'image/jpeg':
                        return (
                            <div>
                                <Image
                                    src={asset.url}
                                    srcSet={asset.gatsbyImageData.images.sources[0].srcSet}
                                    sizes={asset.gatsbyImageData.images.sources[0].sizes}
                                    alt={asset?.description || asset?.title}
                                />
                            </div>
                        )
                    default:
                        return <div>unknown entry </div>
                }
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
                const entry = allEntryTypes[node.data.target.sys.id]

                switch (entry?.internal?.type) {
                    case 'ContentfulTypeformEmbed':
                        return (
                            <div><TypeformEmbed formId={entry.typeformId} /></div>
                        )
                    default:
                        return <span></span>
                }
            },
            [BLOCKS.HR]: () => {
                return (
                    <div>
                        <hr />
                    </div>
                )
            },
            [BLOCKS.PARAGRAPH]: (node, children) => {
                if (node.nodeType === 'paragraph') {
                    return (
                        <Box fontSize={['md', 'lg']} className={node.nodeType}>
                            {children}
                        </Box>
                    )
                }
            },
            [BLOCKS.HEADING_1]: (node, children) => {
                return <h1 className={node.nodeType}>{children}</h1>
            },
            [BLOCKS.HEADING_2]: (node, children) => {
                return <h2 className={node.nodeType}>{children}</h2>
            },
            [BLOCKS.UL_LIST]: (node, children) => {
                return <UnOrderedList className={node.nodeType}>{children}</UnOrderedList>
            },
        },
    }

    return <div>{documentToReactComponents(JSON.parse(body.raw), options)}</div>
}

const extractEmbeddedAssets = (body) => {
    let aux = []

    body?.references?.forEach((ref) => {
        if (ref?.internal?.type === 'ContentfulAsset') {
            aux[ref.contentful_id] = ref
        }
    })

    return aux
}

const extractLinkToPosts = (body) => {
    let aux = []

    body?.references?.forEach((ref) => {
        if (ref?.internal?.type === 'ContentfulPosts') {
            aux[ref.contentful_id] = ref
        }
    })

    return aux
}

const extractLinkToEducationalLevels = (body) => {
    let aux = []

    body?.references?.forEach((ref) => {
        if (ref?.internal?.type === 'ContentfulEducationalLevels') {
            aux[ref.contentful_id] = ref
        }
    })

    return aux
}

const extractLinkToPage = (body) => {
    let aux = []

    body?.references?.forEach((ref) => {
        if (ref?.internal?.type === 'ContentfulPage') {
            aux[ref.contentful_id] = ref
        }
    })

    return aux
}

const extractLinkToRegistration = (body) => {
    let aux = []

    body?.references?.forEach((ref) => {
        if (ref?.internal?.type === 'ContentfulRegistrations') {
            aux[ref.contentful_id] = ref
        }
    })

    return aux
}

const extractTypeformEmbedAssets = (body) => {
    let aux = []

    body?.references?.forEach((ref) => {
        if (ref?.internal?.type === 'ContentfulTypeformEmbed') {
            aux[ref.contentful_id] = ref
        }
    })

    return aux
}

export default RichContent
